import { useDisclosure } from '@mantine/hooks';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useIntegrationEntries } from '../../../../api/integrations-client/integrations-client.hooks';
import type { IntegrationEntry } from '../../../../api/integrations-client/integrations-client.type';
import { useOrganizationStatusesByProjectsAndBoards } from '../../../../api/organization-client/organization-client.hooks';
import type { OrganizationStatusesByProjectsAndBoards } from '../../../../api/organization-client/organization-client.type';
import type { Workflow } from '../../../../api/workflows-client/workflows-client.type';
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useGetWorkflows,
  useUpdateWorkflow,
} from '../../../../api/workflows-client/workflows.client.hooks';
import { StatusLabelsProvider } from '../../../../contexts/status-labels.context';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { UNKNOWN_SUBPROJECT_NAME } from '../board-settings-tab/board-settings.helpers';
import { workflowReducer } from './shared-components/reducer';
import { WorkflowsContainer } from './shared-components/styles';
import { WorkflowsList } from './shared-components/workflows-list';
import { CreateWorkflow } from './workflow/create/create-workflow';
import { DeleteWorkflowModal } from './workflow/edit/delete-workflow-modal';
import { EditWorkflow } from './workflow/edit/edit-workflow';
import { ViewWorkflow } from './workflow/view/view-workflow';
import { getNewWorkflowName } from './workflows.helpers';

/**
 * Data-management Workflows tab: the workflow list plus the view, create, and
 * edit panes for mapping board statuses onto Bloomfilter workflow steps.
 *
 * Responsibilities:
 * 1. Load the org's workflows and its statuses by project and board, merging in
 *    boards from integration entries that have no statuses yet.
 * 2. Hold the in-progress workflow in a reducer and track dirty state so
 *    navigation away can prompt.
 * 3. Provide source-cased status labels scoped to the selected workflow's
 *    boards, so chips display customer casing while the values written to
 *    `external_name` stay exactly as stored.
 *
 * @param {boolean} isDirty - Whether the pane has unsaved edits
 * @param {(isDirty: boolean) => void} setIsDirty - Reports dirty state to the parent tab shell
 * @returns {JSX.Element} The workflows tab
 */
export function Workflows({ isDirty, setIsDirty }: { isDirty: boolean; setIsDirty: (isDirty: boolean) => void }) {
  const organization = useGlobalStore((state) => state.organization);

  const [pageState, setPageState] = useState<'update' | 'create' | 'idle'>('idle');
  const [deleteWorkflowModalOpened, { open: openDeleteWorkflowModal, close: closeDeleteWorkflowModal }] =
    useDisclosure(false);

  const [state, dispatch] = useReducer(workflowReducer, {} as Workflow);

  const { data: workflows, query: workflowsQuery } = useGetWorkflows(organization?.id || '');
  const { statusesByProjectsAndBoards } = useOrganizationStatusesByProjectsAndBoards(organization?.id || '');

  const { data: integrationEntries = [] } = useIntegrationEntries<IntegrationEntry[]>({
    queryKey: ['integration-entries', organization?.id ?? null] as const,
    enabled: !!organization?.id,
    select: (data) => data.filter((integration) => integration.subprojects.length > 0),
  });

  // Merge all boards from integrationEntries
  const allBoardsData = useMemo(() => {
    if (!statusesByProjectsAndBoards) return undefined;
    if (!integrationEntries.length) return statusesByProjectsAndBoards;

    const result: OrganizationStatusesByProjectsAndBoards = {};

    for (const [projectId, project] of Object.entries(statusesByProjectsAndBoards)) {
      result[projectId] = {
        name: project.name,
        subprojects: { ...project.subprojects },
      };
    }

    // Add missing boards from integration entries
    for (const entry of integrationEntries) {
      for (const sub of entry.subprojects) {
        const projectId = sub.project_id;

        if (result[projectId]?.subprojects[sub.id]) continue;

        if (!result[projectId]) {
          result[projectId] = {
            name: sub.project_name || 'Uncategorized',
            subprojects: {},
          };
        }

        result[projectId].subprojects[sub.id] = {
          name: sub.name ?? UNKNOWN_SUBPROJECT_NAME,
          statuses: [],
        };
      }
    }

    return result;
  }, [statusesByProjectsAndBoards, integrationEntries]);

  const { mutate: createWorkflow } = useCreateWorkflow({
    organizationId: organization?.id || '',
    onSuccess: () => {
      workflowsQuery.refetch();
      setPageState('idle');
    },
  });

  const { mutate: updateWorkflow } = useUpdateWorkflow({
    organizationId: organization?.id || '',
    onSuccess: () => {
      workflowsQuery.refetch();
      setPageState('idle');
    },
  });

  const { mutate: deleteWorkflow } = useDeleteWorkflow({
    organizationId: organization?.id || '',
    onSuccess: () => {
      workflowsQuery.refetch();
      setPageState('idle');
    },
  });

  useEffect(() => {
    if (pageState === 'idle') {
      setIsDirty(false);
    }
  }, [pageState, setIsDirty]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(state): ignore
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageState): ignore
  useEffect(() => {
    if (pageState === 'create' || pageState === 'update') {
      setIsDirty(true);
    }
  }, [state, setIsDirty]);

  // If the page is idle, select the first workflow if one is not already selected
  // biome-ignore lint/correctness/useExhaustiveDependencies(state.id): ignore
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageState): ignore
  useEffect(() => {
    if (pageState === 'idle' && workflowsQuery.isSuccess && workflows?.length && allBoardsData) {
      if (state.id === null) {
        dispatch({
          type: 'SET_WORKFLOW',
          payload: workflows[0],
        });
      } else {
        dispatch({
          type: 'SET_WORKFLOW',
          payload: workflows.find((workflow) => workflow.id === state.id) ?? workflows[0],
        });
      }
    }
  }, [workflows, workflowsQuery.isSuccess, allBoardsData]);

  // Source-cased status labels are scoped to the boards on the selected
  // workflow, which is exactly the set of statuses this tab can render.
  const workflowSubprojectIds = useMemo(() => state.subprojects ?? [], [state.subprojects]);

  return (
    <StatusLabelsProvider subprojectIds={workflowSubprojectIds}>
      <WorkflowsContainer>
        <WorkflowsList
          onCreate={
            pageState === 'idle'
              ? () => {
                  dispatch({
                    type: 'SET_WORKFLOW',
                    payload: { organization: organization?.id || '' } as Workflow,
                  });
                  setPageState('create');
                }
              : undefined
          }
          selectedWorkflowId={state.id}
          onSelectWorkflow={(workflow) => {
            if (isDirty) {
              if (
                window.confirm(
                  "This page is asking you to confirm that you want to leave - information you've entered may not be saved.",
                )
              ) {
                dispatch({
                  type: 'SET_WORKFLOW',
                  payload: workflow,
                });
                setPageState('idle');
              }
            } else {
              dispatch({
                type: 'SET_WORKFLOW',
                payload: workflow,
              });
              setPageState('idle');
            }
          }}
          workflows={(() => {
            if (pageState === 'create') {
              const newWorkflowName = getNewWorkflowName(workflows || []);
              return [...(workflows || []), { name: newWorkflowName }] as Workflow[];
            }
            return workflows || [];
          })()}
        />
        {pageState === 'idle' && (
          <ViewWorkflow
            state={state}
            dispatch={dispatch}
            setPageState={setPageState}
            statusesByProjectsAndBoards={allBoardsData}
            workflows={workflows || []}
            workflowsQuery={workflowsQuery}
          />
        )}
        {pageState === 'update' && (
          <EditWorkflow
            state={state}
            dispatch={dispatch}
            setPageState={setPageState}
            isDirty={isDirty}
            handleSelectWorkflow={(workflow: Workflow) => dispatch({ type: 'SET_WORKFLOW', payload: workflow })}
            statusesByProjectsAndBoards={allBoardsData}
            workflows={workflows || []}
            updateWorkflow={updateWorkflow}
            workflowsQuery={workflowsQuery}
            openDeleteWorkflowModal={openDeleteWorkflowModal}
          />
        )}
        {pageState === 'create' && (
          <CreateWorkflow
            state={state}
            dispatch={dispatch}
            setPageState={setPageState}
            isDirty={isDirty}
            handleSelectWorkflow={(workflow: Workflow) => dispatch({ type: 'SET_WORKFLOW', payload: workflow })}
            statusesByProjectsAndBoards={allBoardsData}
            workflows={workflows || []}
            createWorkflow={(workflow) => createWorkflow({ workflow })}
          />
        )}
        <DeleteWorkflowModal
          opened={deleteWorkflowModalOpened}
          workflowName={state.name}
          onClose={closeDeleteWorkflowModal}
          onConfirm={() => {
            closeDeleteWorkflowModal();
            if (!state.id) {
              return;
            }
            deleteWorkflow(
              { workflowId: state.id },
              {
                onSuccess: () => {
                  dispatch({
                    type: 'SET_WORKFLOW',
                    payload: {} as Workflow,
                  });
                  setPageState('idle');
                  workflowsQuery.refetch();
                },
              },
            );
          }}
        />
      </WorkflowsContainer>
    </StatusLabelsProvider>
  );
}
